import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { REGIONS, SERVICE_CATEGORIES } from "@/pages/operator/provider-create/types";

export interface RegistrationFormData {
  company_name: string;
  legal_name: string;
  contact_name: string;
  contact_email: string;
  contact_role: string;
  phone: string;
  website: string;
  invoice_email: string;
  address_line1: string;
  postal_code: string;
  city: string;
  country: string;
  vat_id: string;
  region: string;
  categories: string[];
  remote_supported: boolean;
  sla_tier: string;
  sla_response_hours: number;
  weekly_lead_limit: number;
  reviewer_notes: string;
}

const EMPTY_FORM: RegistrationFormData = {
  company_name: "",
  legal_name: "",
  contact_name: "",
  contact_email: "",
  contact_role: "",
  phone: "",
  website: "",
  invoice_email: "",
  address_line1: "",
  postal_code: "",
  city: "",
  country: "DE",
  vat_id: "",
  region: "",
  categories: [],
  remote_supported: false,
  sla_tier: "NBD",
  sla_response_hours: 24,
  weekly_lead_limit: 5,
  reviewer_notes: "",
};

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: RegistrationFormData) => Promise<void>;
  initialData?: Partial<RegistrationFormData> | null;
  mode: "create" | "edit";
}

export default function RegistrationFormDialog({ open, onClose, onSubmit, initialData, mode }: Props) {
  const [form, setForm] = useState<RegistrationFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initialData ? { ...EMPTY_FORM, ...initialData } : EMPTY_FORM);
    }
  }, [open, initialData]);

  const set = (key: keyof RegistrationFormData, value: any) => setForm((prev) => ({ ...prev, [key]: value }));

  const toggleCategory = (cat: string) => {
    setForm((prev) => ({
      ...prev,
      categories: prev.categories.includes(cat) ? prev.categories.filter((c) => c !== cat) : [...prev.categories, cat],
    }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onSubmit(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Registrierung anlegen" : "Registrierung bearbeiten"}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 py-4">
          <div className="space-y-1.5">
            <Label>Firmenname *</Label>
            <Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Rechtl. Name</Label>
            <Input value={form.legal_name} onChange={(e) => set("legal_name", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Kontaktperson *</Label>
            <Input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>E-Mail *</Label>
            <Input type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Rolle</Label>
            <Input value={form.contact_role} onChange={(e) => set("contact_role", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Telefon</Label>
            <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Website</Label>
            <Input value={form.website} onChange={(e) => set("website", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Rechnungs-E-Mail</Label>
            <Input value={form.invoice_email} onChange={(e) => set("invoice_email", e.target.value)} />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label>Adresse</Label>
            <Input value={form.address_line1} onChange={(e) => set("address_line1", e.target.value)} placeholder="Straße und Hausnummer" />
          </div>
          <div className="space-y-1.5">
            <Label>PLZ</Label>
            <Input value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Stadt</Label>
            <Input value={form.city} onChange={(e) => set("city", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>USt-IdNr.</Label>
            <Input value={form.vat_id} onChange={(e) => set("vat_id", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Hauptregion</Label>
            <select
              className="w-full h-10 px-3 border border-border bg-background text-sm"
              value={form.region}
              onChange={(e) => set("region", e.target.value)}
            >
              <option value="">– wählen –</option>
              {REGIONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          {/* Categories */}
          <div className="col-span-2 space-y-2">
            <Label>Kategorien</Label>
            <div className="flex flex-wrap gap-2">
              {SERVICE_CATEGORIES.map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => toggleCategory(cat.value)}
                  className={`text-xs px-3 py-1 border transition-colors ${
                    form.categories.includes(cat.value)
                      ? "border-accent bg-accent/10 text-accent"
                      : "border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* SLA & limits */}
          <div className="space-y-1.5">
            <Label>SLA-Stufe</Label>
            <select
              className="w-full h-10 px-3 border border-border bg-background text-sm"
              value={form.sla_tier}
              onChange={(e) => set("sla_tier", e.target.value)}
            >
              <option value="NBD">NBD (24h)</option>
              <option value="4H">4H</option>
              <option value="2H">2H</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Wochenlimit</Label>
            <Input type="number" value={form.weekly_lead_limit} onChange={(e) => set("weekly_lead_limit", Number(e.target.value))} />
          </div>
          <div className="col-span-2 flex items-center gap-3">
            <Switch checked={form.remote_supported} onCheckedChange={(v) => set("remote_supported", v)} />
            <Label>Remote-Support möglich</Label>
          </div>

          {/* Notes (edit mode) */}
          {mode === "edit" && (
            <div className="col-span-2 space-y-1.5">
              <Label>Interne Notizen</Label>
              <textarea
                className="w-full min-h-[80px] px-3 py-2 border border-border bg-background text-sm resize-y"
                value={form.reviewer_notes}
                onChange={(e) => set("reviewer_notes", e.target.value)}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="quiet" onClick={onClose}>Abbrechen</Button>
          <Button
            variant="commit"
            onClick={handleSubmit}
            disabled={saving || !form.company_name || !form.contact_name || !form.contact_email}
          >
            {saving ? "Wird gespeichert..." : mode === "create" ? "Anlegen" : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
